/**
 * Album view component.
 * @module components/theme/View/AlbumView
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Container as SemanticContainer,
  GridColumn,
  Segment,
} from 'semantic-ui-react';
import { Button, Modal, Grid } from 'semantic-ui-react';
import { Icon, UniversalLink } from '@plone/volto/components';
import config from '@plone/volto/registry';

import openSVG from '@plone/volto/icons/open.svg';
import aheadSVG from '@plone/volto/icons/ahead.svg';
import backSVG from '@plone/volto/icons/back.svg';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';

/**
 * Album view component class.
 * @function AlbumView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
class AlbumView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      openIndex: undefined,
    };

    this.closeModal = this.closeModal.bind(this);
    this.nextImage = this.nextImage.bind(this);
    this.prevImage = this.prevImage.bind(this);
  }

  closeModal() {
    this.setState({
      openIndex: -1,
    });
  }

  nextImage() {
    const openIndex =
      (this.state.openIndex + 1) % this.props.content.items.length;
    this.setState({
      openIndex,
    });
  }

  prevImage() {
    const openIndex =
      (this.state.openIndex - 1) % this.props.content.items.length;
    this.setState({
      openIndex,
    });
  }

  render() {
    const { content } = this.props;
    const Container =
      config.getComponent({ name: 'Container' }).component || SemanticContainer;
    const PreviewImage = config.getComponent({
      name: 'PreviewImage',
    }).component;

    return (
      <div className="view-wrapper" id="content">
       
        <Grid doubling stackable columns={3} id="content-core" as="ul" className='list-unstyled pt-5 listAlbumImage'>
          {content.items &&
            content.items.map((item, index) => (
              <React.Fragment key={item.url}>
                {item.image_field && (
                  <Modal
                    className="gallery"
                    onClose={this.closeModal}
                    open={this.state.openIndex === index}
                    trigger={
                      <Grid.Column as="li">
                        <div className="imageborder albumImage" >
                          <button type='button' className='albumImage__btn'
                            onClick={() => {
                              this.setState({
                                openIndex: index,
                              });
                            }}>
                            <div className="overflow-hidden rounded-1">
                              <PreviewImage
                                item={item}
                                alt={item.image_caption || item.title}
                                className="ui middle aligned image"
                                responsive={true}
                                loading="lazy"
                              />

                            </div>
                            <div className="d-flex justify-content-end p-2 d-print-none">
                              <FontAwesomeIcon icon={faEye} className="albumImage__icon " />
                            </div>
                          </button>
                        </div>
                      </Grid.Column>
                    }
                    closeIcon
                  >
                    <Modal.Header>
                      <Grid>
                        <Grid.Row>
                          <GridColumn width={10}>{item.title}</GridColumn>
                          <GridColumn width={2} textAlign="right">
                            <UniversalLink
                              href={item.url}
                              title={item['@type']}
                              onClick={this.closeModal}
                            >
                              <Icon size="30px" fitted name={openSVG} />
                              <span className="visually-hidden"><FormattedMessage id="Open" defaultMessage="Open" /></span>
                            </UniversalLink>
                          </GridColumn>
                        </Grid.Row>
                      </Grid>
                    </Modal.Header>
                    <Grid centered verticalAlign="middle">
                      <Grid.Row>
                        <Grid.Column width={2} textAlign="center">
                          <Button
                            className="gallery noborder"
                            onClick={this.nextImage}
                            style={{ margin: 0 }}
                          >
                            <Icon
                              name={backSVG}
                              className="circled"
                              size="30px"
                              style={{ margin: 0 }}
                              aria-hidden="true"
                            />
                            <span className="visually-hidden"><FormattedMessage id="Previous" defaultMessage="Previous" /></span>
                          </Button>
                        </Grid.Column>
                        <Grid.Column width={8}>
                          <Modal.Content image >
                            <PreviewImage
                              item={item}
                              alt={item.image_caption || item.title}
                              onClick={() => {
                                this.setState({
                                  openIndex: index,
                                });
                              }}
                              className="ui image mt-3"
                              responsive={true}
                            />
                            <Modal.Description>
                              <p>{item.description}</p>
                            </Modal.Description>
                          </Modal.Content>
                        </Grid.Column>
                        <Grid.Column width={2} textAlign="center">
                          <Button
                            onClick={this.nextImage}
                            className="gallery noborder"
                            style={{ margin: 0 }}
                          >
                            <Icon
                              name={aheadSVG}
                              className="circled"
                              size="30px"
                              style={{ margin: 0 }}
                            />
                            <span className="visually-hidden"><FormattedMessage id="Next" defaultMessage="Next" /></span>
                          </Button>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Modal>
                )}
              </React.Fragment>
            ))}
        </Grid>
      </div>
    );
  }
}

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
AlbumView.propTypes = {
  /**
   * Content of the object
   */
  content: PropTypes.shape({
    /**
     * Title of the object
     */
    title: PropTypes.string,
    /**
     * Description of the object
     */
    description: PropTypes.string,
    /**
     * Child items of the object
     */
    items: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * Title of the item
         */
        title: PropTypes.string,
        /**
         * Description of the item
         */
        description: PropTypes.string,
        /**
         * Url of the item
         */
        url: PropTypes.string,
        /**
         * Image of the item
         */
        image: PropTypes.object,
        /**
         * Image caption of the item
         */
        image_caption: PropTypes.string,
        /**
         * Type of the item
         */
        '@type': PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default AlbumView;
